import { useEffect, useState, useCallback } from "react";

/**
 * usePWAInstall
 * - Captures beforeinstallprompt for Android/Desktop Chrome-based browsers.
 * - Detects iOS Safari (which has no prompt event) so we can show manual instructions.
 * - Reports whether the app is already running as a standalone PWA.
 */
export function usePWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState("web"); // "web" | "ios" | "android"
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    setPlatform(isIOS ? "ios" : isAndroid ? "android" : "web");

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    try {
      setDismissed(localStorage.getItem("slkc-install-dismissed") === "1");
    } catch (e) {}

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice;
  }, [deferred]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem("slkc-install-dismissed", "1"); } catch (e) {}
    setDismissed(true);
  }, []);

  // Available if a native prompt exists OR user is on iOS Safari (which needs manual instructions).
  const canInstall = !isStandalone && (Boolean(deferred) || platform === "ios");

  return { canInstall, isStandalone, platform, promptInstall, dismissed, dismiss, hasNativePrompt: Boolean(deferred) };
}
